<script setup lang="ts">
import { EButton, EDialog, EField, EInput, formatDate, useToast } from '@erpx/ui'
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'

import AppIcon from '@/components/AppIcon.vue'
import { stepUp } from '@/api/stepup'
import { useAuthStore } from '@/stores/auth'

/**
 * The one place a step-up challenge is answered (CLAUDE.md §6/§13).
 *
 * Mounted once at the root, it appears whenever any request anywhere in the app
 * is refused with `ERR_STEP_UP_REQUIRED`. Three things it is built to do:
 *
 *  1. **Say why.** "Paying suppliers needs your authenticator" tells somebody
 *     what is happening; "Re-authentication required" tells them a system is
 *     unhappy. The reason comes from the request the transport was making.
 *  2. **Not lose the action.** The request is still pending behind this dialog.
 *     A refused code re-prompts — the server spends the code it was shown, so a
 *     replayed one fails even while the authenticator still displays it, and
 *     "wait for the next code" has to be a state, not a dead end.
 *  3. **Send the unenrolled somewhere useful.** An account with no second
 *     factor cannot answer this, so it is offered enrolment instead — before
 *     asking for a code if we already know, and after `ERR_MFA_REQUIRED` if the
 *     server is the one who knows.
 *
 * Accessibility (§11): `EDialog` traps focus, labels itself from its title and
 * restores focus on close; the code field takes focus on open, failures are
 * announced through a live region, and nothing here is reachable by mouse only.
 */

const auth = useAuthStore()
const router = useRouter()
const toast = useToast()
const { t, locale } = useI18n()

/** Where an account with no authenticator goes to get one. */
const ENROL_ROUTE = { path: '/profile', query: { tab: 'security', enrol: '1' } }

const prompt = stepUp.prompt
const code = ref('')
const codeInput = ref<InstanceType<typeof EInput> | null>(null)
/** The last value submitted, so auto-submit does not resend a refused code. */
let submitted = ''

const open = computed(() => prompt.value !== null)

/** §6 makes MFA mandatory for the roles holding these permissions; some have not. */
const enrolled = computed(() => auth.user?.mfaEnabled !== false)

const reason = computed(() => {
  const key = `stepUp.reasons.${prompt.value?.reason ?? 'generic'}`
  return t(key)
})

const waiting = computed(() => prompt.value?.waiting ?? 0)

/**
 * The lockout detail is an RFC 3339 instant ("try again after 2026-…Z"), which
 * is precise and unreadable. Pulled out and rendered on the user's clock it
 * becomes an actual answer to "when can I try again".
 */
const lockedUntil = computed(() => {
  const match = /\d{4}-\d{2}-\d{2}T[\d:.]+(?:Z|[+-]\d{2}:\d{2})/.exec(prompt.value?.detail ?? '')
  if (match === null) return ''
  return formatDate(match[0], { preset: 'time', locale: locale.value, timeZone: auth.timeZone })
})

const failureText = computed(() => {
  const failure = prompt.value?.failure
  if (failure === undefined || failure === null) return ''
  if (failure === 'lockout') {
    return lockedUntil.value === ''
      ? t('stepUp.errors.lockout')
      : t('stepUp.errors.lockoutUntil', { time: lockedUntil.value })
  }
  if (failure === 'code') return t('stepUp.errors.code')
  const detail = prompt.value?.detail ?? ''
  return detail === '' ? t('stepUp.errors.other') : detail
})

/** A locked account cannot be helped by another code; only by waiting. */
const lockedOut = computed(() => prompt.value?.failure === 'lockout')
const busy = computed(() => prompt.value?.busy === true)
const canSubmit = computed(() => !busy.value && !lockedOut.value && code.value.length >= 6)

/** Paste-friendly: strips spaces and separators authenticator apps display. */
function onCode(value: string): void {
  code.value = value.replace(/\D/g, '').slice(0, 6)
  // Submitting the moment a code is complete is what makes this a one-gesture
  // interruption rather than a form. Guarded against resending a value the
  // server has already refused.
  if (code.value.length === 6 && code.value !== submitted && !busy.value && !lockedOut.value) {
    void submit()
  }
}

async function submit(): Promise<void> {
  if (!canSubmit.value) return
  submitted = code.value
  await stepUp.submit(code.value)
  // Only a refusal leaves the prompt open; clearing here readies the field for
  // the next code without wiping a successful one mid-close.
  if (prompt.value !== null) {
    code.value = ''
    await focusCode()
  }
}

function cancel(): void {
  stepUp.cancel()
}

async function focusCode(): Promise<void> {
  await nextTick()
  // One frame after the dialog's own opening focus, which would otherwise land
  // on the close button and steal this back.
  await new Promise((resolve) => setTimeout(resolve, 0))
  codeInput.value?.focus()
}

function goToEnrolment(): void {
  void router.push(ENROL_ROUTE)
  stepUp.cancel()
}

watch(open, (isOpen) => {
  if (!isOpen) {
    code.value = ''
    submitted = ''
    return
  }
  if (enrolled.value) void focusCode()
})

/**
 * When the server answers the challenge with `ERR_MFA_REQUIRED` the account has
 * no factor to re-prove: re-prompting would loop. The waiting requests are
 * already released by the broker; this takes the person somewhere they can act.
 */
stepUp.setEnrolmentHandler(() => {
  toast.warning(t('stepUp.enrol.routedTitle'), t('stepUp.enrol.routedDetail'))
  void router.push(ENROL_ROUTE)
})

onBeforeUnmount(() => {
  stepUp.setEnrolmentHandler(null)
})
</script>

<template>
  <EDialog
    :open="open"
    size="sm"
    persistent
    hide-close
    :title="enrolled ? $t('stepUp.title') : $t('stepUp.enrol.title')"
    :description="reason"
    @close="cancel"
  >
    <div class="stepup">
      <template v-if="enrolled">
        <p class="stepup__lead">{{ $t('stepUp.description') }}</p>

        <p v-if="waiting > 1" class="text-xs muted" data-test="stepup-waiting">
          {{ $t('stepUp.waiting', waiting) }}
        </p>

        <EField
          :label="$t('stepUp.codeLabel')"
          :error="failureText === '' ? null : failureText"
          required
        >
          <EInput
            ref="codeInput"
            :model-value="code"
            inputmode="numeric"
            autocomplete="one-time-code"
            :maxlength="6"
            :disabled="lockedOut"
            numeric
            :placeholder="$t('stepUp.codePlaceholder')"
            :aria-label="$t('stepUp.codeLabel')"
            @update:model-value="onCode"
            @enter="submit"
          />
        </EField>

        <p
          v-if="(prompt?.attempts ?? 0) > 0 && !lockedOut"
          class="stepup__note"
          data-test="stepup-budget"
        >
          <AppIcon name="alert" :size="14" />
          <span>{{ $t('stepUp.attemptsWarning') }}</span>
        </p>
      </template>

      <template v-else>
        <p class="stepup__lead" data-test="stepup-enrol">{{ $t('stepUp.enrol.body') }}</p>
      </template>

      <!-- Announced without stealing focus: the code field keeps it. -->
      <p class="sr-only" role="status" aria-live="polite">{{ failureText }}</p>
    </div>

    <template #footer>
      <EButton variant="ghost" @click="cancel">{{ $t('common.actions.cancel') }}</EButton>
      <EButton v-if="!enrolled" variant="primary" @click="goToEnrolment">
        {{ $t('stepUp.enrol.action') }}
      </EButton>
      <EButton
        v-else
        variant="primary"
        :loading="busy"
        :loading-label="$t('stepUp.verifying')"
        :disabled="!canSubmit"
        @click="submit"
      >
        {{ $t('stepUp.submit') }}
      </EButton>
    </template>
  </EDialog>
</template>

<style>
.stepup {
  display: flex;
  flex-direction: column;
  gap: var(--erpx-space-3);
}

.stepup__lead {
  margin: 0;
  color: var(--erpx-color-text-secondary);
  font-size: var(--erpx-text-sm);
  line-height: var(--erpx-leading-snug);
}

.stepup__note {
  display: flex;
  align-items: flex-start;
  gap: var(--erpx-space-2);
  margin: 0;
  color: var(--erpx-color-text-muted);
  font-size: var(--erpx-text-xs);
  line-height: var(--erpx-leading-snug);
}
</style>
